import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import { toast } from "sonner";

import {
  usePlannedOrder,
  usePlanningRun,
  useRecalculate,
  useShortages,
  type PlanningRun,
} from "@/api/planningRun";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Empty, EmptyDescription, EmptyHeader } from "@/components/ui/empty";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatDate, formatRelativeTime } from "@/utils/formatters";
import { PlanningRunProgressModal } from "@/views/planning/PlanningRunProgressModal";

import { OrderDetail } from "./OrderDetail";
import { OrdersView } from "./OrdersView";
import { ShortagesView } from "./ShortagesView";

export default function PlanningWorkspace() {
  const { t } = useTranslation();
  const runId = Number(useParams().runId);
  const [searchParams, setSearchParams] = useSearchParams();
  const orderId = Number(searchParams.get("order")) || null;
  const [tab, setTab] = useState("orders");
  // Selecting an order anywhere, e.g. in the shortages drawer, shows it.
  const [shownOrderId, setShownOrderId] = useState(orderId);
  if (orderId !== shownOrderId) {
    setShownOrderId(orderId);
    if (orderId) setTab("orders");
  }
  const run = usePlanningRun(runId);
  const order = usePlannedOrder(runId, orderId);
  // Only a 404 reaches here (other errors throw): a recalculation replaced the order.
  const orderGone = order.isError;
  useEffect(() => {
    if (!orderGone) return;
    toast.info(
      t(
        "planningReview.orderReplaced",
        "This order was replaced by a recalculation.",
      ),
      { id: "order-replaced" },
    );
    setSearchParams(
      (params) => {
        params.delete("order");
        return params;
      },
      { replace: true },
    );
  }, [orderGone, setSearchParams, t]);
  const recalc = useRecalculate(runId);
  const shortages = useShortages(runId);
  const [calculating, setCalculating] = useState<PlanningRun | null>(null);

  const startRecalc = () =>
    recalc.mutate(undefined, {
      onSuccess: setCalculating,
      onError: () =>
        toast.error(
          t(
            "planningRuns.progress.recalcFailed",
            "Could not recalculate the run.",
          ),
        ),
    });

  return (
    <Tabs value={tab} onValueChange={setTab} className="h-full gap-0">
      <PageHeader
        className="px-4 pt-3"
        title={t("planningRuns.detail.title", { id: runId })}
        meta={
          run.data && (
            <span className="text-muted-foreground text-xs">
              {t("planningRuns.detail.meta", {
                computedAt: run.data.computedAt
                  ? formatRelativeTime(run.data.computedAt)
                  : "—",
                planDate: formatDate(run.data.planDate),
              })}
            </span>
          )
        }
        actions={
          <Button onClick={startRecalc} disabled={recalc.isPending}>
            {t("planningRuns.actions.recalculate", "Recalculate")}
          </Button>
        }
        tabs={
          <TabsList variant="line">
            <TabsTrigger value="orders">
              {t("planningReview.tabs.orders", "Orders")}
            </TabsTrigger>
            <TabsTrigger value="shortages">
              {t("planningReview.tabs.shortages", "Shortages")}
              {shortages.data && (
                <Badge variant="secondary">{shortages.data.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
        }
      />
      {/* Mounted while hidden, so the order list keeps its filters and page. */}
      <TabsContent
        value="orders"
        forceMount
        className="min-h-0 data-[state=inactive]:hidden"
      >
        <ResizablePanelGroup
          direction="horizontal"
          autoSaveId="planning-review-workspace"
          className="h-full"
        >
          <ResizablePanel defaultSize={38} minSize={28}>
            <OrdersView key={runId} runId={runId} selectedOrderId={orderId} />
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={62} minSize={40}>
            <div className="h-full overflow-auto">
              {order.data ? (
                <OrderDetail order={order.data} />
              ) : (
                <Empty className="h-full">
                  <EmptyHeader>
                    <EmptyDescription>
                      {t(
                        "planningReview.selectOrder",
                        "Select an order to inspect stock",
                      )}
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              )}
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </TabsContent>
      <TabsContent value="shortages" className="min-h-0">
        {run.data && (
          <ShortagesView runId={runId} planDate={run.data.planDate} />
        )}
      </TabsContent>
      <PlanningRunProgressModal
        run={calculating}
        onClose={() => setCalculating(null)}
        onRetry={startRecalc}
      />
    </Tabs>
  );
}
